
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCompany } from '@/contexts/CompanyContext';
import EnhancedLoadingState from '@/components/dashboard/EnhancedLoadingState';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requireSubscription?: boolean;
  requireCompany?: boolean;
  /**
   * When true, keeps children mounted while auth/permission/company checks are loading,
   * rendering a non-destructive loading overlay instead of replacing the whole route.
   * This prevents layout flicker for persistent layouts (sidebar/header).
   */
  keepChildrenMountedWhileLoading?: boolean;
}

// Routes that don't require a company to be active
const COMPANY_EXEMPT_ROUTES = ['/settings', '/onboarding/company'];

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireSuperAdmin = false,
  requireSubscription = true,
  requireCompany = true,
  keepChildrenMountedWhileLoading = false,
}) => {
  const { user, loading, isAdmin, isSuperAdmin, isCompanyAdmin, checkAdminStatus, userRoles } = useAuth();
  const { hasAccess, loading: subscriptionLoading } = useSubscription();
  const { currentCompany, availableCompanies, loading: companyLoading } = useCompany();
  const location = useLocation();
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    if ((requireAdmin || requireSuperAdmin) && user && !loading) {
      const verifyAdminStatus = async () => {
        console.log('ProtectedRoute: Starting admin status verification...', {
          currentRoles: userRoles,
          userId: user.id
        });
        setAdminCheckError(null);
        setAdminCheckComplete(false);
        setRolesLoaded(false);
        
        try {
          // Set a timeout to prevent infinite loading
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Admin status check timeout')), 10000)
          );
          
          await Promise.race([
            checkAdminStatus(),
            timeoutPromise
          ]);
          
          console.log('ProtectedRoute: Admin status verification complete');
          
          // Small delay to ensure state updates have propagated
          setTimeout(() => {
            setRolesLoaded(true);
            setAdminCheckComplete(true);
          }, 50);
          
        } catch (error: any) {
          console.error('ProtectedRoute: Admin status verification failed:', error);
          setAdminCheckError(error.message || 'Failed to verify admin status');
          setAdminCheckComplete(true);
          setRolesLoaded(true);
        }
      };
      
      verifyAdminStatus();
    } else {
      setAdminCheckComplete(true);
      setRolesLoaded(true);
    }
  }, [user, loading, requireAdmin, requireSuperAdmin, checkAdminStatus]);

  // Check if current route is exempt from company requirement
  const isCompanyExemptRoute = COMPANY_EXEMPT_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  const isCheckingAccess =
    loading ||
    subscriptionLoading ||
    companyLoading ||
    ((requireAdmin || requireSuperAdmin) && (!adminCheckComplete || !rolesLoaded));

  // Loading state while checking authentication, admin status, subscription, or company.
  // For persistent layouts we keep children mounted with NO overlay - let page content handle its own loading.
  if (isCheckingAccess) {
    if (keepChildrenMountedWhileLoading) {
      // Just render children - no overlay, no full-screen loading
      // The layout stays mounted and individual pages handle their own loading states
      return <>{children}</>;
    }

    return (
      <EnhancedLoadingState loading={true} dataLoadError={null} stage="permissions">
        {null}
      </EnhancedLoadingState>
    );
  }

  if (!user) {
    console.log('ProtectedRoute: No user, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Check company requirement (after auth but before subscription)
  // Skip this check for exempt routes
  if (requireCompany && !isCompanyExemptRoute) {
    const hasNoCompany = !currentCompany && availableCompanies.length === 0;
    if (hasNoCompany) {
      console.log('ProtectedRoute: No active company, redirecting to /onboarding/company');
      return <Navigate to="/onboarding/company" replace />;
    }
  }

  // Redirect away from onboarding/company if user already has a company
  if (location.pathname === '/onboarding/company') {
    const hasCompany = currentCompany || availableCompanies.length > 0;
    if (hasCompany) {
      console.log('ProtectedRoute: User already has a company, redirecting to /dashboard');
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check subscription access - redirect to billing if no access
  if (requireSubscription && !hasAccess) {
    // Don't redirect if already on settings page
    if (!location.pathname.startsWith('/settings')) {
      console.log('ProtectedRoute: No subscription access, redirecting to billing');
      return <Navigate to="/settings?tab=billing" replace />;
    }
  }

  // Handle admin check errors
  if (adminCheckError) {
    console.log('ProtectedRoute: Admin check error:', adminCheckError);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-muted border border-border text-foreground px-4 py-3 rounded-md mb-4">
            <strong className="font-bold">Permission Check Failed</strong>
            <span className="block sm:inline"> Unable to verify your permissions. Please try again.</span>
          </div>
          <p className="text-muted-foreground mb-4">
            Error: {adminCheckError}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={() => window.location.reload()}>Retry</Button>
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Only check admin status AFTER both checks are complete AND roles are loaded
  if (adminCheckComplete && rolesLoaded && requireSuperAdmin) {
    const hasSuperAdminAccess = isSuperAdmin();
    console.log('ProtectedRoute: Super admin check', {
      required: true,
      userRoles,
      hasSuperAdminAccess
    });
    
    if (!hasSuperAdminAccess) {
      console.log('ProtectedRoute: Super admin required but user is not super admin');
      return (
          <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
              <div className="bg-muted border border-border text-foreground px-4 py-3 rounded-md mb-4">
              <strong className="font-bold">Access Denied!</strong>
              <span className="block sm:inline"> You need super admin privileges to access this page.</span>
            </div>
              <p className="text-muted-foreground mb-4">
              Please contact a platform administrator if you believe you should have access to this area.
            </p>
              <Button variant="outline" onClick={() => window.history.back()}>
                Go Back
              </Button>
          </div>
        </div>
      );
    }
  }

  // Only check admin status AFTER both checks are complete AND roles are loaded
  if (adminCheckComplete && rolesLoaded && requireAdmin) {
    const hasAdminAccess = isAdmin();
    console.log('ProtectedRoute: Admin check', {
      required: true,
      userRoles,
      hasAdminAccess,
      isCompanyAdmin: isCompanyAdmin()
    });
    
    if (!hasAdminAccess) {
      console.log('ProtectedRoute: Admin required but user is not admin');
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="bg-muted border border-border text-foreground px-4 py-3 rounded-md mb-4">
              <strong className="font-bold">Access Denied!</strong>
              <span className="block sm:inline"> You need admin privileges to access this page.</span>
            </div>
            <p className="text-muted-foreground mb-4">
              {isCompanyAdmin() ? 
                "You have company admin access, but this page requires global admin privileges." :
                "Please contact an administrator if you believe you should have access to this area."
              }
            </p>
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </div>
        </div>
      );
    }
  }

  console.log('ProtectedRoute: Access granted');
  return <>{children}</>;
};

export default ProtectedRoute;
